import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { isNativeShell } from '../_helpers/native-shell';
import { IMessage } from '../_interface/crossAppMessaging';
import { CrossAppMessagingService } from './crossApp-messaging.service';

/**
 * Rezultat pouzdanog slanja. `queued` znači da native nije uspeo da isporuči odmah pa je
 * zahtev stavljen u perzistentni outbox i biće poslat kasnije (offline scenario).
 */
export interface ReliableHttpResult {
  status: number;
  body: any;
  queued?: boolean;
}

/**
 * Univerzalni "reliable HTTP" — kad smo u native (MAUI) shell-u, zahtev (method/url/body) se predaje
 * native-u koji garantuje isporuku: pokuša odmah → vrati odgovor; ako ne uspe → snimi u outbox i
 * šalje periodično dok ne prođe. Van shell-a (čist web) ide direktan HttpClient (bez offline garancije).
 *
 * Native je agnostičan na sadržaj — samo prosleđuje HTTP. Idempotenciju (da retry ne duplira)
 * mora da obezbedi server/pozivalac.
 */
@Injectable({ providedIn: 'root' })
export class ReliableHttpService {
  /** Keširana provera da li native APK podržava http.send (probe se radi jednom). */
  private supports?: Promise<boolean>;

  constructor(
    private http: HttpClient,
    private crossApp: CrossAppMessagingService
  ) { }

  async send(method: string, url: string, body?: any, headers?: { [key: string]: string }): Promise<ReliableHttpResult> {
    // http.send koristimo SAMO ako native shell to podržava (novi APK). Stari shell nema handler —
    // probe vrati false pa idemo na direktan HTTP (kao i ranije), bez visenja na timeout.
    if (await this.nativeSupportsHttpSend()) {
      const msg: IMessage = {
        action: 'http.send',
        data: { method, url, headers: headers ?? {}, body }
      };

      // 10 min timeout — native odmah pokuša; ako padne, vraća {queued:true} bez čekanja.
      const res = await this.crossApp.sendRawMessage(msg, true, 600000, true);
      return (res ?? { status: 0, body: null, queued: true }) as ReliableHttpResult;
    }

    // Čist web ili stari shell: direktan HTTP (bez outbox-a).
    const resp = await firstValueFrom(
      this.http.request(method, url, { body, headers, observe: 'response' })
    );
    return { status: resp.status, body: resp.body };
  }

  /**
   * Probe: pita native da li podržava pouzdan HTTP transport. Stari APK nema `http.getCapabilities`
   * responder → CrossAppCommunication vrati null → false → direktan HTTP. Rezultat se kešira.
   */
  private nativeSupportsHttpSend(): Promise<boolean> {
    if (!isNativeShell()) {
      return Promise.resolve(false);
    }
    if (!this.supports) {
      this.supports = this.crossApp
        .sendRawMessage({ action: 'http.getCapabilities' }, true, 10000, true)
        .then((res: any) => !!(res && res.httpSend))
        .catch(() => false);
    }
    return this.supports;
  }
}
